import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Users, HelpCircle } from 'lucide-react';
import { organizationsService } from '../services/organizationsService';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import Label from '../components/ui/Label';
import { api } from '../services/authService';
import { getCookie, setCookie } from '../services/cookieService';
import { getDashboardCacheDays } from '../services/cookieConfigService';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

/** Matches organization name for FastCube (aggregate signal counts across providers). */
const FASTCUBE_ORG = 'FastCube';

/** Sentinel for dashboard domain filter (org-level aggregate; do not send to API as domain). */
const DOMAIN_SLICE_ALL = '__all__';

const DOMAIN_SLICE_OPTIONS = [
  { value: DOMAIN_SLICE_ALL, label: 'All' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'apple', label: 'Apple' },
  { value: 'yahoo', label: 'Yahoo' },
  { value: 'gmail', label: 'Gmail' },
  { value: 'aol', label: 'AOL' },
  { value: 'other', label: 'Other' },
];

const PANEL_TOOLTIPS = {
  activeProviders: 'Total number of signal providers actively sending signals.',
  uniqueEmails: 'Total number of unique email addresses, includes both opted-in and opted-out.',
  knownProfiles: 'Number of MD5s that match to a profile',
  deliverableEmails: 'Total number of emails that have a valid opt-in date, no opt-out date and are not on the DNM list.',
  signalsAcrossDbsFastCube: 'Total count of all signals across all providers.',
  signalsAcrossDbsOrg: 'Total count of MD5s within your organization that have a signal',
  dnm: 'Total number of emails in the Do Not Mail list.',
  signalProviders: "Percentages represent each provider's share of the total number of signals.",
  domainBreakdown: 'Domain percentages of all unique emails.',
  activeHashes: 'Count of MD5s by recency bucket, given the most recent signal date from each MD5 across all signal providers.',
  activityScore: `Percentages represent how many emails fall into each activity bucket.

Score Breakdown:
10: 0 days (today)
9: ~1-3 days
8: ~4-10 days
7: ~11-25 days
6: ~26-50 days
5: ~51-90 days
4: ~91-151 days
3: ~152-220 days
2: ~221-300 days
1: ~301-365+ days
0: No signal date`,
};

const TOOLTIP_WIDTH_PX = 288; // w-72 = 18rem

function PanelTitleWithTooltip({ children, tooltip }) {
  const [visible, setVisible] = React.useState(false);
  const [alignRight, setAlignRight] = React.useState(false);
  const ref = React.useRef(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const wouldOverflowRight = rect.left + TOOLTIP_WIDTH_PX > window.innerWidth;
      setAlignRight(wouldOverflowRight);
    }
    setVisible(true);
  };

  const handleMouseLeave = () => setVisible(false);

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <span className="ml-1 cursor-help text-gray-400 hover:text-gray-600 flex-shrink-0" aria-label={tooltip}>
        <HelpCircle className="w-4 h-4" />
      </span>
      <span
        role="tooltip"
        className={`absolute bottom-full mb-1 z-50 w-72 p-2.5 text-xs font-normal text-left text-white bg-gray-900 rounded shadow-lg whitespace-pre-line ${visible ? 'block' : 'hidden'} ${alignRight ? 'right-0' : 'left-0'}`}
      >
        {tooltip}
      </span>
    </span>
  );
}

function cacheKey(organizationId, suffix, domainSlice) {
  const safe = organizationId != null && organizationId !== '' ? String(organizationId) : '';
  const dom =
    domainSlice && domainSlice !== DOMAIN_SLICE_ALL
      ? `_domain_${String(domainSlice).replace(/\s+/g, '_')}`
      : '';
  return `${safe}${dom}_${suffix}`;
}

function toChartData(fields, preserveOrder = false) {
  const normalized = fields
    .map((item) => ({ ...item, value: Number(item.value) || 0 }))
    .filter(({ value }) => value > 0);
  const sorted = preserveOrder ? normalized : [...normalized].sort((a, b) => b.value - a.value);
  return sorted.map((item, i) => ({ ...item, color: CHART_COLORS[i % CHART_COLORS.length] }));
}

function formatCount(n) {
  if (n == null || typeof n !== 'number') return '';
  if (n >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(3)} T`;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(3)} B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(3)} M`;
  return n.toLocaleString();
}

function Dashboard() {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [selectedDomainSlice, setSelectedDomainSlice] = useState(DOMAIN_SLICE_ALL);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [signalProvidersData, setSignalProvidersData] = useState(null);
  const [activeHashesData, setActiveHashesData] = useState(null);
  const [activityScoreData, setActivityScoreData] = useState(null);
  const [domainSummaryData, setDomainSummaryData] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartsError, setChartsError] = useState(null);
  const [activeProviderCount, setActiveProviderCount] = useState(null);
  const [uniqueEmailCount, setUniqueEmailCount] = useState(null);
  const [knownProfileMd5Count, setKnownProfileMd5Count] = useState(null);
  const [dnmEmailCount, setDnmEmailCount] = useState(null);
  const [deliverableEmailsCount, setDeliverableEmailsCount] = useState(null);
  const [totalSignalCount, setTotalSignalCount] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);

  // Get display name based on user data
  const getDisplayName = () => {
    // 1) If there's a first and last name, use that
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    // 2) If there's only an email, remove after the '@' and split based on "." and "_". Capitalize the parts.
    if (user?.email) {
      const localPart = user.email.split('@')[0];
      // Split on both '.' and '_'
      const parts = localPart.split(/[._]/);
      return parts
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
    }
    
    // 3) If no first name, last name, or email, return null (will show "Welcome back!" without name)
    return null;
  };

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);
        const savedSelected = organizationsService.getSelectedOrganization('name');
        if (savedSelected && orgs.some(o => o.name === savedSelected)) {
          setSelectedOrganization(savedSelected);
          organizationsService.setSelectedOrganization(savedSelected, 'name', orgs);
        } else if (orgs.length > 0) {
          const defaultOrg = orgs[0].name;
          setSelectedOrganization(defaultOrg);
          organizationsService.setSelectedOrganization(defaultOrg, 'name', orgs);
        }
      } catch {
        // ignore; keep defaults
      } finally {
        setOrganizationsLoading(false);
      }
    };
    loadOrganizations();
  }, []);

  // If selected organization is not in the current Postgres-backed list, clear it (and charts when none left)
  useEffect(() => {
    if (organizationsLoading || !organizations?.length) return;
    const isInList = organizations.some((o) => o.name === selectedOrganization);
    if (!selectedOrganization || isInList) return;
    const next = organizations[0]?.name ?? '';
    setSelectedOrganization(next);
    organizationsService.setSelectedOrganization(next, 'name', organizations);
    if (!next) {
      setSignalProvidersData(null);
      setActiveHashesData(null);
      setActivityScoreData(null);
      setDomainSummaryData(null);
      setChartsError(null);
      setActiveProviderCount(null);
      setUniqueEmailCount(null);
      setDnmEmailCount(null);
      setDeliverableEmailsCount(null);
      setTotalSignalCount(null);
    }
  }, [organizationsLoading, organizations, selectedOrganization]);

  useEffect(() => {
    const loadDashboard = async () => {
      if (!selectedOrganization) {
        setChartsLoading(false);
        setCountsLoading(false);
        setSignalProvidersData(null);
        setActiveHashesData(null);
        setActivityScoreData(null);
        setDomainSummaryData(null);
        setChartsError(null);
        setActiveProviderCount(null);
        setUniqueEmailCount(null);
        setDnmEmailCount(null);
        setDeliverableEmailsCount(null);
        setTotalSignalCount(null);
        return;
      }
      setSignalProvidersData(null);
      setActiveHashesData(null);
      setActivityScoreData(null);
      setDomainSummaryData(null);
      setChartsError(null);
      setActiveProviderCount(null);
      setUniqueEmailCount(null);
      setDnmEmailCount(null);
      setDeliverableEmailsCount(null);
      setTotalSignalCount(null);
      setChartsLoading(true);
      setCountsLoading(true);
      const orgRecord = organizations.find((o) => o.name === selectedOrganization);
      const organizationId = orgRecord?.id;
      if (organizationId == null || organizationId === '') {
        setChartsLoading(false);
        setCountsLoading(false);
        return;
      }
      const keyDashboard = cacheKey(organizationId, 'dashboard', selectedDomainSlice);

      const applyProviders = (d) => {
        if (!d) return;
        setSignalProvidersData(toChartData([
          { name: 'LiveIntent', value: d.live_intent_md5_ct ?? 0 },
          { name: 'iPost', value: d.ipost_md5_ct ?? 0 },
          { name: 'Traverse', value: d.traverse_md5_ct ?? 0 },
          { name: 'Other', value: d.other_provider_md5_ct ?? 0 },
        ]));
      };
      const applyHashes = (d) => {
        if (!d) return;
        setActiveHashesData(toChartData([
          { name: '0–30 days', value: d.md5_last_signal_0_30d_ct ?? 0 },
          { name: '31–60 days', value: d.md5_last_signal_31_60d_ct ?? 0 },
          { name: '61–90 days', value: d.md5_last_signal_61_90d_ct ?? 0 },
          { name: '91+ days', value: d.md5_last_signal_91_plus_ct ?? 0 },
          { name: 'No Signal', value: d.md5_emails_with_no_signal_ct ?? 0 },
        ], true));
      };
      const applyActivityScore = (d) => {
        if (!d) return;
        setActivityScoreData(toChartData([
          { name: '9–10', value: d.activity_score_9_10 ?? 0 },
          { name: '7–8', value: d.activity_score_7_8 ?? 0 },
          { name: '5–6', value: d.activity_score_5_6 ?? 0 },
          { name: '3–4', value: d.activity_score_3_4 ?? 0 },
          { name: '1–2', value: d.activity_score_1_2 ?? 0 },
          { name: 'No Signal', value: d.md5_emails_with_no_signal_ct ?? 0 },
        ], true));
      };
      const applyDomain = (d) => {
        if (!d) return;
        setDomainSummaryData(toChartData([
          { name: 'Yahoo', value: d.yahoo_md5_ct ?? 0 },
          { name: 'Gmail', value: d.gmail_md5_ct ?? 0 },
          { name: 'AOL', value: d.aol_md5_ct ?? 0 },
          { name: 'Microsoft', value: d.microsoft_md5_ct ?? 0 },
          { name: 'Apple', value: d.apple_md5_ct ?? 0 },
          { name: 'Other', value: d.other_domain_md5_ct ?? 0 },
        ]));
      };
      const applyData = (data) => {
        applyProviders(data.signal_providers);
        applyHashes(data.active_hashes);
        applyDomain(data.domain_summary);
        applyActivityScore(data.activity_score);
        setActiveProviderCount(data.active_provider_count ?? null);
        setUniqueEmailCount(data.unique_email_count ?? null);
        setKnownProfileMd5Count(data.known_profile_md5_count ?? null);
        setDnmEmailCount(data.dnm_email_count ?? null);
        setDeliverableEmailsCount(data.deliverable_emails_count ?? null);
        setTotalSignalCount(data.total_signal_count ?? null);
      };

      try {
        const cached = getCookie(keyDashboard);
        if (cached) {
          try {
            const data = JSON.parse(cached);
            applyData(data);
            setChartsLoading(false);
            setCountsLoading(false);
            return;
          } catch { /* ignore */ }
        }

        const params = { organization_id: organizationId };
        if (selectedDomainSlice !== DOMAIN_SLICE_ALL) {
          params.domain = selectedDomainSlice;
        }
        const { data } = await api.get('/api/v2/signal_analysis/dashboard', {
          params,
        });
        applyData(data);
        const hasData =
          data.signal_providers != null ||
          data.active_hashes != null ||
          data.domain_summary != null ||
          data.activity_score != null ||
          data.unique_email_count != null ||
          data.known_profile_md5_count != null ||
          data.dnm_email_count != null ||
          data.deliverable_emails_count != null ||
          data.total_signal_count != null ||
          data.active_provider_count != null;
        if (hasData) {
          setCookie(keyDashboard, JSON.stringify(data), getDashboardCacheDays());
        }
      } catch {
        setChartsError('Unable to load chart data');
      } finally {
        setChartsLoading(false);
        setCountsLoading(false);
      }
    };
    loadDashboard();
  }, [selectedOrganization, selectedDomainSlice, organizations]);

  const ChartCard = ({ title, titleTooltip, data, loading, iconLeft, iconLeftBg }) => (
    <div className="bg-white shadow rounded-lg p-4 min-w-0 overflow-visible">
      <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
        {iconLeft && (
          <span
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: iconLeftBg }}
          >
            <img src={iconLeft} alt="" className="w-4 h-4" />
          </span>
        )}
        {titleTooltip ? (
          <PanelTitleWithTooltip tooltip={titleTooltip}>
            <span>{title}</span>
          </PanelTitleWithTooltip>
        ) : (
          title
        )}
      </h3>
      {loading ? (
        <div className="h-[220px] flex items-center justify-center text-gray-500 text-base">Loading...</div>
      ) : !data || data.length === 0 ? (
        <div className="h-[220px] flex items-center justify-center text-gray-500 text-base">No data currently available</div>
      ) : (() => {
        const total = data.reduce((acc, d) => acc + d.value, 0);
        return (
          <div className="flex items-center gap-3 w-full" style={{ height: 220 }}>
            <div className="flex-1 min-w-0" style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={85}
                    paddingAngle={0}
                    minAngle={1}
                    labelLine={false}
                    label={({ percent, x, y }) => {
                      if (percent < 0.02) return null;
                      const pct = `${(percent * 100).toFixed(1)}%`;
                      const padding = 4;
                      const fontSize = 10;
                      const width = Math.max(pct.length * 6 + padding * 2, 28);
                      const height = fontSize + padding * 2;
                      return (
                        <g>
                          <rect
                            x={x - width / 2}
                            y={y - height / 2}
                            width={width}
                            height={height}
                            fill="white"
                            stroke="#e5e7eb"
                            strokeWidth={1}
                            rx={2}
                          />
                          <text
                            x={x}
                            y={y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={fontSize}
                            fill="#374151"
                          >
                            {pct}
                          </text>
                        </g>
                      );
                    }}
                  >
                    {data.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      total ? `${((100 * value) / total).toFixed(1)}%` : value,
                      name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Custom legend on the right, in data order (largest first) */}
            <div className="flex flex-col gap-y-1 flex-shrink-0" style={{ fontSize: 11 }}>
              {data.map((entry, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-2 h-2 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span>{entry.name}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white shadow rounded-lg overflow-visible">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center min-w-0">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div className="ml-4 min-w-0">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {(() => {
                    const displayName = getDisplayName();
                    return displayName ? `Welcome back, ${displayName}!` : 'Welcome back!';
                  })()}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Monitor your data integration performance and metrics
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0 w-full sm:w-auto">
              <div className="w-full sm:w-56">
                <Label htmlFor="dashboard-organization" className="text-sm font-medium text-gray-700">
                  Organization
                </Label>
                {organizationsLoading ? (
                  <p className="mt-1 text-sm text-gray-500">Loading...</p>
                ) : organizations.length === 0 ? (
                  <FunctionalSelect
                    value=""
                    onValueChange={() => {}}
                    placeholder="No organizations available"
                    className="mt-1 w-full"
                    disabled
                  />
                ) : (
                  <FunctionalSelect
                    id="dashboard-organization"
                    value={organizationsService.getSelectedOptionValue(organizations, selectedOrganization, 'name')}
                    onValueChange={(value) => {
                      const parsed = organizationsService.parseOrganizationOptionValue(value);
                      if (parsed) {
                        setSelectedOrganization(parsed.name);
                        organizationsService.setSelectedOrganization(parsed.name, 'name', [{ name: parsed.name, id: parsed.id }]);
                      } else {
                        setSelectedOrganization(value);
                        organizationsService.setSelectedOrganization(value, 'name', organizations);
                      }
                    }}
                    placeholder="Select organization"
                    className="mt-1 w-full"
                  >
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </FunctionalSelect>
                )}
              </div>
              <div className="w-full sm:w-56">
                <Label htmlFor="dashboard-domain" className="text-sm font-medium text-gray-700">
                  Domain
                </Label>
                <FunctionalSelect
                  id="dashboard-domain"
                  value={selectedDomainSlice}
                  onValueChange={setSelectedDomainSlice}
                  placeholder="All"
                  className="mt-1 w-full"
                  disabled={organizationsLoading || organizations.length === 0}
                >
                  {DOMAIN_SLICE_OPTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </FunctionalSelect>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Count panels: row 1 = Active Providers, Total Signals (FastCube) / Total MD5s with Signals (other orgs), DNM Count; row 2 = Unique Emails, Known Profiles, Deliverable Emails */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center justify-between">
            <PanelTitleWithTooltip tooltip={PANEL_TOOLTIPS.activeProviders}>
              <span>Active Providers</span>
            </PanelTitleWithTooltip>
            <img src="/database.svg" alt="" className="w-6 h-6 flex-shrink-0" />
          </h3>
          {countsLoading ? (
            <p className="text-gray-500 text-base">Loading...</p>
          ) : activeProviderCount !== null ? (
            <p className="text-3xl font-semibold text-gray-900">
              {formatCount(activeProviderCount)}
            </p>
          ) : (
            <p className="text-gray-500 text-base">No data currently available</p>
          )}
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center justify-between">
            <PanelTitleWithTooltip
              tooltip={
                selectedOrganization === FASTCUBE_ORG
                  ? PANEL_TOOLTIPS.signalsAcrossDbsFastCube
                  : PANEL_TOOLTIPS.signalsAcrossDbsOrg
              }
            >
              <span>
                {selectedOrganization === FASTCUBE_ORG
                  ? 'Total Signals'
                  : 'Total MD5s with Signals'}
              </span>
            </PanelTitleWithTooltip>
            <img src="/activity.svg" alt="" className="w-6 h-6 flex-shrink-0" />
          </h3>
          {countsLoading ? (
            <p className="text-gray-500 text-base">Loading...</p>
          ) : totalSignalCount !== null ? (
            <p className="text-3xl font-semibold text-gray-900">
              {formatCount(totalSignalCount)}
            </p>
          ) : (
            <p className="text-gray-500 text-base">No data currently available</p>
          )}
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center justify-between">
            <PanelTitleWithTooltip tooltip={PANEL_TOOLTIPS.dnm}>
              <span>DNM Count</span>
            </PanelTitleWithTooltip>
            <img src="/info.svg" alt="" className="w-6 h-6 flex-shrink-0" />
          </h3>
          {countsLoading ? (
            <p className="text-gray-500 text-base">Loading...</p>
          ) : dnmEmailCount !== null ? (
            <p className="text-3xl font-semibold text-gray-900">
              {formatCount(dnmEmailCount)}
            </p>
          ) : (
            <p className="text-gray-500 text-base">No data currently available</p>
          )}
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center justify-between">
            <PanelTitleWithTooltip tooltip={PANEL_TOOLTIPS.uniqueEmails}>
              <span>Unique Emails</span>
            </PanelTitleWithTooltip>
            <img src="/mail.svg" alt="" className="w-6 h-6 flex-shrink-0" />
          </h3>
          {countsLoading ? (
            <p className="text-gray-500 text-base">Loading...</p>
          ) : uniqueEmailCount !== null ? (
            <p className="text-3xl font-semibold text-gray-900">
              {formatCount(uniqueEmailCount)}
            </p>
          ) : (
            <p className="text-gray-500 text-base">No data currently available</p>
          )}
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center justify-between">
            <PanelTitleWithTooltip tooltip={PANEL_TOOLTIPS.knownProfiles}>
              <span>Known Profiles</span>
            </PanelTitleWithTooltip>
            <img src="/user-check.svg" alt="" className="w-6 h-6 flex-shrink-0" />
          </h3>
          {countsLoading ? (
            <p className="text-gray-500 text-base">Loading...</p>
          ) : knownProfileMd5Count !== null ? (
            <p className="text-3xl font-semibold text-gray-900">
              {formatCount(knownProfileMd5Count)}
            </p>
          ) : (
            <p className="text-gray-500 text-base">No data currently available</p>
          )}
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center justify-between">
            <PanelTitleWithTooltip tooltip={PANEL_TOOLTIPS.deliverableEmails}>
              <span>Deliverable Emails</span>
            </PanelTitleWithTooltip>
            <img src="/inbox.svg" alt="" className="w-6 h-6 flex-shrink-0" />
          </h3>
          {countsLoading ? (
            <p className="text-gray-500 text-base">Loading...</p>
          ) : deliverableEmailsCount !== null ? (
            <p className="text-3xl font-semibold text-gray-900">
              {formatCount(deliverableEmailsCount)}
            </p>
          ) : (
            <p className="text-gray-500 text-base">No data currently available</p>
          )}
        </div>
      </div>

      {/* Signal analysis doughnut charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard
          title="Signal Providers"
          titleTooltip={PANEL_TOOLTIPS.signalProviders}
          data={signalProvidersData}
          loading={chartsLoading}
          iconLeft="/radio.svg"
          iconLeftBg="#E5EFFE"
        />
        <ChartCard
          title="Domain Breakdown"
          titleTooltip={PANEL_TOOLTIPS.domainBreakdown}
          data={domainSummaryData}
          loading={chartsLoading}
          iconLeft="/globe.svg"
          iconLeftBg="#E8F6F5"
        />
        <ChartCard
          title="Active Hashes"
          titleTooltip={PANEL_TOOLTIPS.activeHashes}
          data={activeHashesData}
          loading={chartsLoading}
          iconLeft="/check-circle.svg"
          iconLeftBg="#FEF5ED"
        />
        <ChartCard
          title="Activity Score"
          titleTooltip={PANEL_TOOLTIPS.activityScore}
          data={activityScoreData}
          loading={chartsLoading}
          iconLeft="/star.svg"
          iconLeftBg="#FEEDF5"
        />
      </div>
      {chartsError && (
        <p className="text-sm text-amber-600">{chartsError}</p>
      )}
    </div>
  );
}

export default Dashboard;
